import type { ReactElement } from 'react';
import { useRef } from 'react';
import type { AudienceSegment, Proposal } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import {
  audienceCards,
  audienceImageQuery,
  patchAudienceSegment,
  type AudienceCardViewModel,
} from '../../lib/audiencePresentation';
import { AUDIENCE_ICON_COMPONENTS } from '../../lib/audienceIcons';
import { deliveryKind, momentumJourneyStage, pricedLine } from '../../lib/plan';
import { fitScaleStyle, useFitScale } from '../../hooks/useFitScale';
import { useBindings } from '../../state/BindingsContext';
import { MOMENTUM_STAGES } from './Motifs';
import { Frame, HeadRow } from './primitives';

/**
 * Which momentum journey stage's label a persona card wears, only when a
 * tactic has actually claimed this segment (slide audit, Slide 6: "stage
 * label only when a tactic claims the segment"). v3 drops v2's index-cycling
 * placeholder fallback, so a persona no tactic references yet wears no stage
 * label at all rather than a fabricated one.
 */
function personaStageLabel(proposal: Proposal, cardId: string): string | null {
  const tactic = proposal.strategy.tactics.find((t) => t.audienceSegmentIds?.includes(cardId));
  if (!tactic) return null;
  if (tactic.funnelStage) return tactic.funnelStage;
  const kind = deliveryKind(pricedLine(proposal, tactic.productId));
  const stage = momentumJourneyStage(tactic.productId, kind);
  return MOMENTUM_STAGES.find((s) => s.key === stage)?.label ?? null;
}

/**
 * The size row: icon badge + the segment's VisitIQ count + PEOPLE. Shown
 * whenever an estimate exists, whatever its sizing state (product decision
 * 2026-09-29: counts always show, never marked stale); omitted only when
 * there is no number at all, since a count is never invented.
 */
function PersonaSize({ card }: { card: AudienceCardViewModel }): ReactElement {
  const Glyph = AUDIENCE_ICON_COMPONENTS[card.icon];
  return (
    <div className="mv3-persona-size">
      <span className="mv3-persona-size-badge" aria-hidden="true">
        <Glyph weight="regular" />
      </span>
      <span className="mv3-persona-size-figure">
        <span className="mv3-persona-size-value">{card.sizeLabel}</span>
        <span className="mv3-persona-size-unit">People</span>
      </span>
    </div>
  );
}

function PersonaCard({
  card,
  proposal,
  actions,
  readOnly,
}: {
  card: AudienceCardViewModel;
  proposal: Proposal;
  actions: PageProps['actions'];
  readOnly: boolean;
}): ReactElement {
  const resolve = useBindings();
  const updateSegment = (patch: Partial<AudienceSegment>, label: string) => {
    actions.setAudience({ segments: patchAudienceSegment(proposal.audience, card.id, patch) }, label);
  };
  const stageLabel = personaStageLabel(proposal, card.id);
  const copyInnerRef = useRef<HTMLDivElement | null>(null);
  // Robustness floor for K3a: the name/stage-label must survive even a
  // deliberately over-length description (schema-level 160-char cap is
  // another stream's job; this is the belt-and-suspenders guard on the
  // page itself). A real stored proposal (b6f35be2) has 800+ char
  // descriptions - 5x the 160-char budget - so the floor is set low
  // enough that even that content fully fits rather than merely avoiding
  // the worst of the clipping; scaling never clips or ellipsizes text
  // itself, see useFitScale.
  const scale = useFitScale(copyInnerRef, { min: 0.4 });

  return (
    <article className="mv3-persona-card">
      <div className="mv3-persona-copy">
        <div className="mv3-persona-copy-inner" ref={copyInnerRef} style={fitScaleStyle(scale)}>
          {stageLabel ? <span className="mv3-persona-stage">{resolve(stageLabel)}</span> : null}
          {card.sizeLabel ? <PersonaSize card={card} /> : null}
          <EditableText
            value={card.name}
            field="body"
            placeholder="Segment name"
            readOnly={readOnly}
            variant="headline"
            onSave={(name) => updateSegment({ name }, `Renamed ${card.name}`)}
          />
          <EditableText
            value={card.description}
            field="body"
            placeholder="Describe this audience in one or two sentences"
            readOnly={readOnly}
            onSave={(description) => updateSegment({ description }, `Edited ${card.name} description`)}
          />
        </div>
      </div>
      <div className="mv3-persona-photo">
        {card.imageUrl ? <img src={card.imageUrl} alt="" aria-hidden="true" /> : <div className="audience-photo-placeholder" aria-hidden="true" />}
        {!readOnly ? (
          <ImageSlot
            url={card.segment.imageUrl}
            alt={card.name}
            readOnly={false}
            hideImage
            proposalId={proposal.proposalId}
            defaultQuery={audienceImageQuery(proposal, card.segment)}
            emptyState="neutral"
            className="mv3-persona-image-affordance"
            onSave={(imageUrl) => updateSegment({ imageUrl }, `Changed ${card.name} image`)}
          />
        ) : null}
      </div>
    </article>
  );
}

/**
 * audience_profiles (docs/momentum-v3-slides.md, `audience_profiles`): FULL
 * light, 2-4 image-forward persona cards, no rationale band. Persona
 * name/description/photo bind exactly like momentum_v1's
 * `AudienceProfilesPage` (same `audienceCards`/`patchAudienceSegment`
 * helpers, same `setAudience` mutation). Per the slide audit (Slide 6): the
 * "Best use" pill and the bottom Audience Rationale band are gone entirely -
 * and so is the tagline under the title - so cards grow into that reclaimed
 * space via `mv3-persona-grid-{count}` (2 cards -> one larger row of two).
 * The mint stage label is the one thing kept, but only when a real tactic
 * claims the segment (no placeholder cycling), and it renders in
 * high-contrast dark text, never green-on-white.
 */
export function AudienceProfilesPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const cards = audienceCards(proposal.audience);
  const countClass = cards.length > 0 ? ` mv3-persona-grid-${Math.min(cards.length, 4)}` : '';

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-audience-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <HeadRow title="Audience" />
          {cards.length > 0 ? (
            <div className={`mv3-persona-grid${countClass}`}>
              {cards.map((card) => (
                <PersonaCard key={card.id} card={card} proposal={proposal} actions={actions} readOnly={readOnly} />
              ))}
            </div>
          ) : (
            <p className="note">Audience segments pending.</p>
          )}
        </div>
      </Frame>
    </PageShell>
  );
}
